<template>
  <b-modal
    :id="MODAL_ID"
    :title="title"
    size="lg"
    centered
    ok-title="Save"
    @ok.prevent="onSave"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between">
        <h5 class="modal-title d-flex justify-content-between align-items-center flex-wrap">
          {{ title }}
        </h5>
      </div>
    </template>
    
    <div class="my-3" />
    <b-row>
      <b-col cols="6">
        <label>Customers</label>
        <Multiselect
          v-model="selectedCustomer"
          track-by="name"
          label="name"
          placeholder="Select Customer"
          :options="customers"
          @input="onChangeCustomer"
        />
      </b-col>
      <b-col
        cols="6"
        class="align-content-end"
      >
        <Multiselect
          v-model="tags"
          placeholder="Filter Templates"
          multiple
          :loading="isLoading"
          :options="getTags"
          :close-on-select="false"
          :show-labels="false"
          @input="onChangeTags"
        />
      </b-col>
      <b-col cols="12">
        <div class="d-flex justify-content-between mt-3">
          <div class="d-flex">
            <div class="mx-3" />
          </div>
          <b-checkbox
            v-model="galleryView"
            class="float-right"
            switch
            @change="$refs.templatesSelect.activate()"
          >
            Gallery view
          </b-checkbox>
        </div>

        <div class="d-flex">
          <Multiselect
            ref="templatesSelect"
            v-model="selectedTemplate"
            :class="['templates-dropdown', {'gallery-view': galleryView}]"
            track-by="name"
            label="name"
            :placeholder="`Select Template${isMultiSizes ? 's' : ''}`"
            :multiple="isMultiSizes"
            :loading="isLoading"
            :options="getTemplatesOptionsFormatted"
            :close-on-select="!isMultiSizes"
            :show-labels="false"
          >
            <template #selection="{ values, isOpen }">
              <span
                v-if="values.length"
                v-show="!isOpen"
                class="multiselect__single"
              >
                {{ values.length }} selected
              </span>
            </template>
            <template #option="props">
              <div
                v-if="props.option.name !== 'Select all'"
                class="select-template-thumbnail-container"
              >
                <div class="select-template-thumbnail">
                  <img
                    :src="props.option.thumbnail"
                    :alt="props.option.name"
                  >
                </div>
              </div>
              <span>{{ props.option.$groupLabel || props.option.name }}</span>
            </template>
          </Multiselect>
        </div>
      </b-col>
    </b-row>
    <div class="my-3" />
    <b-row>
      <b-col cols="6">
        <b-form-row>
          <b-form-checkbox
            v-model="swapAddAsNewTemplate"
            :disabled="applyToAllProducts"
          >
            Add as new template
          </b-form-checkbox>
        </b-form-row>
        <b-form-row class="mt-2">
          <b-form-checkbox
            v-model="applyToAllProducts"
            @change="onApplyToAllChange"
          >
            Apply to all products
          </b-form-checkbox>
        </b-form-row>
      </b-col>
    </b-row>
    
    <template #modal-footer="{ cancel, ok }">
      <div class="w-100 d-flex justify-content-end">
        <div class="d-flex">
          <b-button
            @click="cancel"
          >
            Cancel
          </b-button>
          <div class="mx-1" />
          <b-button
            variant="primary"
            :disabled="isSaving"
            @click="ok"
          >
            Save
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {swapTemplateEventBroker, EVENTS} from "@frontend/group/modules/swap-template/event-broker";
import {mapGetters} from "vuex";
import Multiselect from "vue-multiselect";
import TEMPLATE_API from "@frontend/services/api/template";

const MODAL_ID = 'swap-template-modal';

export default {
  components: { Multiselect },
  props: {
    customers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      MODAL_ID,
      title: 'Swap Template',
      isMultiSizes: false,
      isSaving: false,
      preview: {},
      selectedCustomer: null,
      selectedTemplate: null,
      templates: {},
      tags: [],
      isLoading: false,
      galleryView: false,
      swapAddAsNewTemplate: false,
      applyToAllProducts: false,
      templatesFilteredByTags: {},
    }
  },
  computed: {
    ...mapGetters('layout',
      ['selectedInstance']
    ),
    getTemplatesOptionsFormatted() {
      return this.getTemplatesOptions.map(option => {
        if (option.width && option.height) {
          return {
            ...option,
            name: `${option.name} (${option.width}x${option.height})`
          };
        }
        return option;
      });
    },
    getTemplatesOptions: function () {
      return this.getTemplates();
    },
    getTags() {
      const tags = this.templates[this.selectedCustomer?.id]
        ?.filter(item => item.tags)
        ?.map(item => item.tags);

      return [...new Set(tags)].length ? [...new Set(tags)] : [];
    },
  },
  mounted() {
    swapTemplateEventBroker.on(EVENTS.INIT, this.init.bind(this));
    swapTemplateEventBroker.on(EVENTS.CLOSED, this.onClose.bind(this));
  },
  methods: {
    init({ title, preview }) {
      this.preview = preview;
      this.title = title;
      this.isSaving = false;
      this.applyToAllProducts = false;
      this.swapAddAsNewTemplate = false;

      const customerId = preview.data.customer_id;
      const templateId = preview.data.template_id;

      if (preview.data.swapAddAsNewTemplate) {
        this.swapAddAsNewTemplate = true;
      }

      this.selectedCustomer = this.customers.find(c => c.id === customerId);
      
      if (this.selectedCustomer) {
        this.onChangeCustomer(this.selectedCustomer).then(() => {
          const allTemplates = this.templates[customerId] || [];
          const matched = allTemplates.find(t => t.id === templateId);
          if (matched) {
            this.selectedTemplate = matched;
         } 
        });
      }

      this.$bvModal.show(MODAL_ID);
    },
    onSave() {
      //check ability for swap
      //all fields from target should be present in original
      if (!this.isTemplateCompatible()) {
        return;
      }
      this.isSaving = true;

      swapTemplateEventBroker.fire(EVENTS.SAVED, {
        objects: this.objects,
        targetTemplate: this.selectedTemplate,
        swapAddAsNewTemplate: this.swapAddAsNewTemplate,
        swapSelectedCustomerId: this.selectedCustomer.id,
        applyToAllProducts: this.applyToAllProducts,
      })
    },
    onClose() {
      this.$nextTick(() => {
        this.$bvModal.hide(MODAL_ID);
      });
    },
    async onChangeCustomer(selectedCustomer) {
      const selectedCustomerId = selectedCustomer.id;
      this.tags = [];
      this.onChangeTags()
      if (this.templates[selectedCustomerId]) return;

      this.selectedTemplate = null;
      await this.fetchTemplates(selectedCustomerId)
    },
    getTemplates() {
      const templates = this.templatesFilteredByTags[this.selectedCustomer?.id] || [];

      return templates;
    },
    onChangeTags() {
      let filteredTemplates = [];

      if (!this.tags.length) {
        return this.templatesFilteredByTags = {
          ...this.templatesFilteredByTags,
          [this.selectedCustomer?.id]: this.templates[this.selectedCustomer?.id]
        }
      }

      this.tags.forEach(tag => {
        const templatesByTag = this.templates[this.selectedCustomer?.id].filter(item => item.tags === tag);

        filteredTemplates = filteredTemplates.concat(templatesByTag);
      });

      this.templatesFilteredByTags[this.selectedCustomer?.id] = filteredTemplates;
    },
    async fetchTemplates(id = null) {
      const selectedCustomerId = id || this.value.customer.id
      this.isLoading = true;

      try {
        const { data } = await TEMPLATE_API.getTemplatesByCustomer(selectedCustomerId);

        this.templates = {
          ...this.templates,
          [selectedCustomerId]: data
        };

        this.templatesFilteredByTags = {
          ...this.templatesFilteredByTags,
          [selectedCustomerId]: data
        };

      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    onApplyToAllChange(val) {
      if (val) {
        this.swapAddAsNewTemplate = false;
      }
    },
    isTemplateCompatible() {
      //check ability for swap
      const originalFields = (this.preview?.template?.fields || []).map(f => f.name);
      const targetFields = (this.selectedTemplate?.fields || []).map(f => f.name);

      // check if all fields presents in origin
      const allFieldsExist = targetFields.every(fieldName => originalFields.includes(fieldName));

      if (!allFieldsExist) {
        this.$bvToast.toast('Selected template is not compatible with current layout. Not all fields exist.', {
          title: 'Swap Error',
          variant: 'danger',
          solid: true
        });
        return false;
      }

      return true;
    },
  }
}
</script>

<style scoped lang="scss">
</style>
